import { useEffect, useState } from "react";
import { Loader2, Phone, Mail, Trash2, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS = ["pending", "approved", "rejected"];
const COLORS = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
};

export const BrokersTab = () => {
  const [items, setItems] = useState(null);

  const load = () => api.get("/admin/realestate/brokers").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/realestate/brokers/${id}`, { status }); await load(); toast.success(`Marked ${status}`); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this broker registration?")) return;
    try { await api.delete(`/admin/realestate/brokers/${id}`); await load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  if (items === null) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" /></div>;

  return (
    <div data-testid="re-brokers-tab">
      <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Broker Registrations</h2>
      <p className="mt-1 text-xs text-[#91837C]">{items.length} total · {items.filter((i) => i.status === "pending").length} pending</p>
      <div className="mt-5 space-y-3">
        {items.map((b) => (
          <div key={b.id} className="glass rounded-2xl p-4" data-testid={`re-broker-${b.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#1A1615]">{b.name} {b.agency ? <span className="font-normal text-[#91837C]">· {b.agency}</span> : null}</p>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#5A504B]">
                  <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 hover:text-[#B38B2D]"><Phone className="h-3 w-3" /> {b.phone}</a>
                  <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1 hover:text-[#B38B2D]"><Mail className="h-3 w-3" /> {b.email}</a>
                  {b.rera_brn && <span className="inline-flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> {b.rera_brn}</span>}
                </div>
                {b.areas && <p className="mt-1.5 text-xs text-[#5A504B]">Areas: {b.areas}</p>}
                {b.note && <p className="mt-2 rounded-lg bg-[#F8F3E6] px-3 py-2 text-xs text-[#5A504B]">{b.note}</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.12em] ${COLORS[b.status] || COLORS.pending}`}>{b.status}</span>
                <button onClick={() => remove(b.id)} aria-label="Delete" data-testid={`re-broker-delete-${b.id}`} className="flex h-8 w-8 items-center justify-center rounded-full border border-red-200 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {STATUS.map((s) => (
                <button key={s} onClick={() => setStatus(b.id, s)} data-testid={`re-broker-${b.id}-status-${s}`}
                  className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors ${b.status === s ? "bg-[#1A1615] text-[#F3E5AB]" : "border border-[#E5D9CF] text-[#5A504B] hover:bg-[#F5EBE6]"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="glass rounded-2xl p-6 text-sm font-light text-[#91837C]">No broker registrations yet.</p>}
      </div>
    </div>
  );
};
